import {
  BottomSheetBackdrop,
  BottomSheetFlatList,
  BottomSheetHandle,
  BottomSheetModal,
  BottomSheetTextInput,
  type BottomSheetBackdropProps,
  type BottomSheetFlatListMethods,
  type BottomSheetHandleProps
} from '@gorhom/bottom-sheet'
import { useFocusEffect } from '@react-navigation/native'
import { IconCheck, IconChevronDown } from '@tabler/icons-react-native'
import { findIndex, truncate } from 'lodash'
import {
  ComponentRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref
} from 'react'
import { TextInput, useWindowDimensions, View, type ListRenderItemInfo } from 'react-native'

import {
  BottomSheetHeader,
  Button,
  ButtonIcon,
  ButtonText,
  FormInput,
  HStack,
  Icon,
  Input,
  Pressable,
  Text,
  VStack,
  type ButtonProps,
  type FormInputProps,
  type InputProps
} from '@/components'
import { useBottomSheetContainerStyle, useFocusableRef } from '@/hooks'
import {
  blackBottomSheetModalBackgroundStyle,
  blackBottomSheetModalHandleIndicatorStyle,
  blackBottomSheetModalHandleStyle,
  PROFILE_GENDER_OTHER_MAX
} from '@/lib'

import { type SelectOption } from './types'

export type SingleSelectOption = Omit<SelectOption, 'value'> & { value: string | null }

// Props handed to a custom picker target via `renderTarget`. The target is the tappable element
// that opens the bottom sheet; the default is a single-line Button, but consumers (e.g. prompts)
// can render their own (e.g. a multi-line, wrapping field).
export type SingleSelectTargetProps = {
  accessibilityLabel: string
  displayValue?: string | null
  isDisabled?: boolean
  isExpanded: boolean
  onPress: () => void
  placeholder: string
  ref: Ref<View>
}

export interface SingleSelectInputProps extends Omit<FormInputProps, 'children'> {
  buttonProps?: ButtonProps
  displayValue?: string
  inputProps?: Omit<InputProps, 'children' | 'isReadOnly' | 'isDisabled' | 'isFocused'>
  onBlur?: () => void
  onChange: (value: string | null) => void
  onClose?: () => void
  onOtherChange?: (value: string) => void
  options: SingleSelectOption[]
  otherValue?: string | null | undefined
  placeholder?: string
  renderTarget?: (props: SingleSelectTargetProps) => ReactNode
  value?: string | number | null | undefined
  valueIndex?: number
}

const ITEM_HEIGHT = 40 // h-10

export type SingleSelectInputRef = ComponentRef<typeof Button>

export const SingleSelectInput = forwardRef<SingleSelectInputRef, SingleSelectInputProps>(
  (
    {
      accessibilityLabel,
      buttonProps,
      displayValue: _displayValue,
      error,
      helper,
      inputProps,
      isDisabled,
      isRequired,
      label,
      onBlur,
      onChange,
      onClose,
      onOtherChange,
      options,
      otherValue: _otherValue,
      placeholder = 'Choose Option',
      renderTarget,
      value: _value,
      valueIndex,
      ...props
    },
    ref
  ) => {
    const dimensions = useWindowDimensions()
    const [buttonRef, focusButton] = useFocusableRef()
    const [headerRef, focusHeader] = useFocusableRef()
    const [otherRef, focusOther] = useFocusableRef<TextInput>()
    const [isExpanded, setIsExpanded] = useState(false)
    const listRef = useRef<BottomSheetFlatListMethods>(null)
    const bottomSheetModalRef = useRef<BottomSheetModal | null>(null)
    // measured option heights keyed by option value, so scroll-to-selected lines up even when
    // options wrap to multiple lines (the list has variable item heights, so a fixed getItemLayout
    // would mis-position the scroll target)
    const itemHeights = useRef<Record<string, number>>({})
    const [value, setValue] = useState(_value != null ? String(_value) : null)
    const [otherValue, setOtherValue] = useState(_otherValue != null ? String(_otherValue) : null)
    const { selectedIndex, selectedOption } = useMemo(() => {
      const selectedIndex = valueIndex ?? findIndex(options, (option) => option.value === value)
      const selectedOption = selectedIndex !== -1 ? options[selectedIndex] : null

      return { selectedIndex, selectedOption }
    }, [valueIndex, options, value])
    const [activeOption, setActiveOption] = useState(() => selectedOption || options[0])
    const displayValue = useMemo(
      // the custom text is optional, so an "other" option with no text still shows its own label
      () => _displayValue || (selectedOption?.isOther ? otherValue || selectedOption?.label : selectedOption?.label),
      [_displayValue, otherValue, selectedOption]
    )
    const onPressOrFocus = useCallback(() => bottomSheetModalRef.current?.present(), [])
    const targetAccessibilityLabel = useMemo(
      () =>
        `${accessibilityLabel ?? `${label}${isRequired ? '. Required' : ''}`}${helper ? `. ${helper}` : ''}. ${displayValue ?? 'Nothing selected'}`,
      [accessibilityLabel, label, isRequired, helper, displayValue]
    )
    // selects without a visible `label` (e.g. prompts) still need a name for the sheet header and
    // accessibility strings; fall back to the accessibilityLabel before the generic 'Options'
    const optionsTitle = label ?? accessibilityLabel ?? 'Options'

    useEffect(() => {
      setOtherValue(_otherValue != null ? String(_otherValue) : null)
      setValue(_value != null ? String(_value) : null)
    }, [_otherValue, _value])
    useImperativeHandle(ref, () => ({
      ...buttonRef.current,
      focus: onPressOrFocus
    }))

    return (
      <FormInput
        error={error}
        helper={helper}
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        {...props}
      >
        {renderTarget ? (
          renderTarget({
            accessibilityLabel: targetAccessibilityLabel,
            displayValue,
            isDisabled,
            isExpanded,
            onPress: onPressOrFocus,
            placeholder,
            ref: buttonRef
          })
        ) : (
          <Button
            accessibilityHint={`Tap to open a list of ${optionsTitle} options`}
            accessibilityLabel={targetAccessibilityLabel}
            accessibilityRole='combobox'
            accessibilityState={{
              disabled: isDisabled,
              expanded: isExpanded
            }}
            aria-activedescendant={activeOption?.value}
            aria-errormessage={error}
            aria-haspopup='listbox'
            aria-required={isRequired}
            className='ios:leading-[0px] justify-between bg-white px-3 py-0'
            isDisabled={isDisabled}
            onPress={onPressOrFocus}
            ref={buttonRef}
            role='combobox'
            {...buttonProps}
          >
            <ButtonText
              accessibilityElementsHidden
              aria-hidden
              importantForAccessibility='no-hide-descendants'
              className='font-poppinsRegular text-base color-black'
            >
              {displayValue || placeholder || 'Select an option'}
            </ButtonText>
            <ButtonIcon
              aria-hidden
              as={IconChevronDown}
              className='color-black'
            />
          </Button>
        )}
        <BottomSheetModal
          accessible={false}
          accessibilityLabel=''
          accessibilityRole='none'
          backdropComponent={useCallback(
            (props: BottomSheetBackdropProps) => (
              <BottomSheetBackdrop
                {...props}
                accessible={false}
                accessibilityLabel=''
                accessibilityRole='none'
                appearsOnIndex={0}
                disappearsOnIndex={-1}
                importantForAccessibility='no-hide-descendants'
              />
            ),
            []
          )}
          backgroundStyle={blackBottomSheetModalBackgroundStyle}
          containerStyle={useBottomSheetContainerStyle()}
          enableDismissOnClose
          enableDynamicSizing
          enablePanDownToClose
          handleComponent={useCallback(
            (props: BottomSheetHandleProps) => (
              <BottomSheetHandle
                {...props}
                accessible={false}
                accessibilityLabel=''
                accessibilityRole='none'
                importantForAccessibility='no-hide-descendants'
              />
            ),
            []
          )}
          handleIndicatorStyle={blackBottomSheetModalHandleIndicatorStyle}
          handleStyle={blackBottomSheetModalHandleStyle}
          importantForAccessibility='no'
          keyboardBehavior='interactive'
          keyboardBlurBehavior='restore'
          maxDynamicContentSize={useMemo(() => dimensions.height * 0.5, [dimensions.height])}
          onChange={useCallback(
            (position: number) => {
              // opened
              if (position === 0) {
                setIsExpanded(true)
                focusHeader()

                if (selectedIndex > 0) {
                  listRef.current?.scrollToIndex({
                    animated: true,
                    index: selectedIndex
                  })
                }
              }
            },
            [focusHeader, selectedIndex]
          )}
          onDismiss={useCallback(() => {
            setIsExpanded(false)
            focusButton()
            onBlur?.()
          }, [focusButton, onBlur])}
          ref={bottomSheetModalRef}
        >
          <BottomSheetFlatList
            accessible={false}
            accessibilityLabel=''
            accessibilityRole='none'
            bounces={false}
            contentContainerClassName='pb-safe-or-4 web:pb-4'
            data={options}
            extraData={{
              headerRef,
              isDisabled,
              isRequired,
              label,
              onChange,
              onOtherChange,
              options,
              otherValue,
              selectedOption,
              value
            }}
            focusHook={useFocusEffect}
            keyboardDismissMode='interactive'
            keyboardShouldPersistTaps='handled'
            keyExtractor={useCallback((item: SingleSelectOption) => item.value ?? '__null__', [])}
            getItemLayout={useCallback(
              (_: ArrayLike<SingleSelectOption> | null | undefined, index: number) => {
                let offset = 0

                for (let i = 0; i < index; i++) {
                  offset += itemHeights.current[options[i]?.value ?? '__null__'] ?? ITEM_HEIGHT
                }

                return {
                  index,
                  length: itemHeights.current[options[index]?.value ?? '__null__'] ?? ITEM_HEIGHT,
                  offset
                }
              },
              [options]
            )}
            onScrollToIndexFailed={useCallback(
              ({
                index,
                averageItemLength
              }: {
                index: number
                highestMeasuredFrameIndex: number
                averageItemLength: number
              }) => {
                listRef.current?.scrollToOffset({
                  animated: true,
                  offset: averageItemLength * index
                })
                setTimeout(
                  () =>
                    listRef.current?.scrollToIndex({
                      animated: true,
                      index
                    }),
                  50
                )
              },
              []
            )}
            ref={listRef}
            renderItem={useCallback(
              ({ item }: ListRenderItemInfo<SingleSelectOption>) => {
                const isSelected = item.value === selectedOption?.value
                const otherAccessibilityLabel = item.isOther
                  ? `${item.label} custom text, optional, ${PROFILE_GENDER_OTHER_MAX} characters max`
                  : undefined
                const pressable = (
                  <Pressable
                    accessibilityLabel={item.label}
                    accessibilityRole='radio'
                    accessibilityState={{
                      checked: isSelected,
                      disabled: isDisabled
                    }}
                    aria-checked={isSelected}
                    className='flex-1 flex-row items-center justify-between gap-2 px-4 py-2 hover:bg-background-50 active:bg-background-100 data-[focus=true]:bg-background-100 web:data-[focus-visible=true]:bg-background-100 web:data-[focus-visible=true]:outline-indicator-primary data-[disabled=true]:opacity-40 data-[disabled=true]:web:pointer-events-auto data-[disabled=true]:web:cursor-not-allowed'
                    onFocus={() => setActiveOption(item)}
                    onPress={() => {
                      setValue(item.value)

                      if (!item.isOther) {
                        setOtherValue('')
                        onChange(item.value)
                        onOtherChange?.('')
                        bottomSheetModalRef.current?.dismiss()
                      } else {
                        // don't dismiss the bottomsheet when the isOther option is selected, but rather focus the text input
                        onChange(item.value)

                        if (otherRef.current) {
                          focusOther()
                        }
                      }
                    }}
                    style={{
                      minHeight: ITEM_HEIGHT
                    }}
                  >
                    <Text className='flex-1 color-white'>{item.label}</Text>
                    {isSelected ? (
                      <Icon
                        aria-hidden
                        as={IconCheck}
                        className='color-white'
                        size='xl'
                      />
                    ) : null}
                  </Pressable>
                )

                return (
                  <HStack
                    className='items-center justify-center'
                    onLayout={(event) => {
                      itemHeights.current[item.value ?? '__null__'] = event.nativeEvent.layout.height
                    }}
                  >
                    {item.isOther && onOtherChange ? (
                      <VStack className='w-full justify-center'>
                        {pressable}
                        <HStack className='items-center gap-3 px-4'>
                          <Input className='flex-1'>
                            <BottomSheetTextInput
                              accessibilityLabel={otherAccessibilityLabel}
                              accessibilityState={{
                                disabled: isDisabled
                              }}
                              autoCapitalize='words'
                              autoComplete='off'
                              autoCorrect
                              className='ios:leading-[0px] h-full flex-1 px-3 py-0 font-poppinsRegular text-typography-900 web:cursor-text web:data-[disabled=true]:cursor-not-allowed'
                              editable={!isDisabled}
                              maxLength={PROFILE_GENDER_OTHER_MAX}
                              onChangeText={(otherValue) => {
                                const truncatedOtherValue = truncate(otherValue, {
                                  length: PROFILE_GENDER_OTHER_MAX,
                                  omission: ''
                                })
                                setValue(item.value)
                                setOtherValue(truncatedOtherValue)
                                onChange(item.value)
                                onOtherChange?.(truncatedOtherValue)
                              }}
                              onFocus={() => setActiveOption(item)}
                              placeholder='Enter something else here'
                              // @ts-ignore false error
                              ref={otherRef}
                              value={otherValue ?? ''}
                            />
                          </Input>
                          <Text
                            accessible={false}
                            accessibilityElementsHidden
                            aria-hidden
                            importantForAccessibility='no-hide-descendants'
                            className='text-xs text-white'
                          >
                            ({otherValue?.length ?? 0}/{PROFILE_GENDER_OTHER_MAX})
                          </Text>
                        </HStack>
                      </VStack>
                    ) : (
                      pressable
                    )}
                  </HStack>
                )
              },
              [focusOther, isDisabled, onChange, onOtherChange, otherRef, otherValue, selectedOption?.value]
            )}
            ListHeaderComponent={useCallback(
              () => (
                <BottomSheetHeader
                  closeAccessibilityLabel={`Close ${optionsTitle} options`}
                  onClosePress={() => bottomSheetModalRef.current?.dismiss()}
                  ref={headerRef}
                  title={optionsTitle}
                  titleAccessibilityLabel={`${optionsTitle} options, ${options.length} items`}
                />
              ),
              [headerRef, optionsTitle, options.length]
            )}
            stickyHeaderIndices={[0]}
          />
        </BottomSheetModal>
      </FormInput>
    )
  }
)

SingleSelectInput.displayName = 'SingleSelectInput'
